import React, { useMemo, useState } from 'react';
import { ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { NotificationItem } from '../components/NotificationItem';
import type { InAppNotification } from '../types';

interface NotificationsScreenProps {
  readonly notifications: InAppNotification[];
  readonly onNotificationPress: (notification: InAppNotification) => void;
  readonly onMarkAllAsRead: () => void;
  readonly onEditNotifications: () => void;
}

export const NotificationsScreen: React.FC<NotificationsScreenProps> = ({
  notifications,
  onNotificationPress,
  onMarkAllAsRead,
  onEditNotifications,
}) => {
  const [expandedReadItems, setExpandedReadItems] = useState<Record<string, boolean>>({});

  const unreadNotifications = useMemo(
    () => notifications.filter(notification => !notification.isRead),
    [notifications]
  );
  const readNotifications = useMemo(
    () => notifications.filter(notification => notification.isRead),
    [notifications]
  );

  const toggleReadNotificationExpansion = (notificationId: string): void => {
    setExpandedReadItems(previousValue => ({
      ...previousValue,
      [notificationId]: !previousValue[notificationId],
    }));
  };

  if (notifications.length === 0) {
    return (
      <View style={styles.emptyContainer}>
        <Text style={styles.emptyTitle}>Notifications</Text>
        <Text style={styles.emptyText}>You have no notifications yet.</Text>
        <TouchableOpacity style={styles.editButton} onPress={onEditNotifications} activeOpacity={0.8}>
          <Text style={styles.editButtonText}>Edit notifications</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
      <View style={styles.headerRow}>
        <Text style={styles.title}>Notifications</Text>
        <View style={styles.headerActions}>
          <TouchableOpacity style={styles.headerEditButton} onPress={onEditNotifications} activeOpacity={0.8}>
            <Text style={styles.editButtonText}>Edit notifications</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.markAllButton} onPress={onMarkAllAsRead} activeOpacity={0.8}>
            <Text style={styles.markAllText}>Mark all as read</Text>
          </TouchableOpacity>
        </View>
      </View>

      {unreadNotifications.length > 0 && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Unread</Text>
          {unreadNotifications.map(notification => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onPress={onNotificationPress}
            />
          ))}
        </View>
      )}

      {readNotifications.length > 0 && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Earlier</Text>
          {readNotifications.map(notification => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onPress={onNotificationPress}
              isExpanded={Boolean(expandedReadItems[notification.id])}
              onToggleExpanded={toggleReadNotificationExpansion}
            />
          ))}
        </View>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  contentContainer: {
    padding: 20,
    paddingBottom: 32,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  title: {
    fontSize: 26,
    color: '#111827',
    fontFamily: 'Montserrat_700Bold',
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  headerEditButton: {
    borderWidth: 1,
    borderColor: '#2563EB',
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 8,
    backgroundColor: '#EFF6FF',
  },
  editButton: {
    borderWidth: 1,
    borderColor: '#2563EB',
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 8,
    backgroundColor: '#EFF6FF',
    marginTop: 12,
  },
  editButtonText: {
    fontSize: 12,
    color: '#1D4ED8',
    fontFamily: 'Montserrat_600SemiBold',
  },
  markAllButton: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 8,
    backgroundColor: '#FFFFFF',
  },
  markAllText: {
    fontSize: 12,
    color: '#374151',
    fontFamily: 'Montserrat_600SemiBold',
  },
  section: {
    marginBottom: 14,
  },
  sectionTitle: {
    fontSize: 14,
    color: '#374151',
    fontFamily: 'Montserrat_600SemiBold',
    marginBottom: 8,
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F8FAFC',
    padding: 20,
  },
  emptyTitle: {
    fontSize: 24,
    color: '#111827',
    fontFamily: 'Montserrat_700Bold',
    marginBottom: 8,
  },
  emptyText: {
    fontSize: 14,
    color: '#6B7280',
    fontFamily: 'Montserrat_500Medium',
  },
});
